// @ts-check
import kushki from '../../../engines/kushki';

/**
 * @typedef {Object} CardParams
 * @property {string} holderName - Card holder name
 * @property {string} cardNumber - Card number
 * @property {string} cvv2 - Card verification value
 * @property {string} expirationMonth - Card expiration month
 * @property {string} expirationYear - Card expiration year
 *
 * Interface for a Kushki strategy.
 * @typedef {Object} KushkiStrategy
 * @property {function(CardParams, { amount: number, currency: string }): Promise<Object>} createPayload - Create payment payload.
 */

function KushkiStrategy() {
  /**
   * Create payload for Kushki card strategy.
   */
  const createPayload = async (cardParams, { amount, currency }) => {
    const response = await kushki.createCardToken({
      amount,
      currency,
      card: {
        name: cardParams.holderName,
        number: cardParams.cardNumber,
        cvc: cardParams.cvv2,
        expiryMonth: cardParams.expirationMonth,
        expiryYear: cardParams.expirationYear.slice(2, 4),
      },
    });

    // Check if 3DS validation is required
    const { token, secureId, security } = response;

    if (security?.authRequired) {
      try {
        // Call requestValidate3DS which might trigger the Kushki modal
        await kushki.requestValidate3DS({
          secureId,
          security,
        });
        // If validation is successful, proceed with the token
      } catch (validationError) {
        // Handle 3DS validation error (e.g., user closed modal, incorrect OTP)
        // Re-throw or handle specific Kushki 3DS error codes (like E005)
        console.error('Kushki 3DS Validation Error:', validationError);
        // Map Kushki error to a user-friendly message or a specific error object
        throw new Error(validationError.message || '3DS authentication failed');
      }
    }

    // If 3DS was not required or validation was successful, return the token payload
    return {
      credit_card_tokens: {
        kushki_token: token,
      },
    };
  };

  return { createPayload };
}

const kushkiStrategy = KushkiStrategy();

export default kushkiStrategy;
